import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getConfig } from '../lib/config';
import { useSiteSettings } from '../lib/siteSettings';
import * as publicApi from '../api/public';
import { ErrorAlert, Field, Spinner } from '../components/ui';
import { ui, cn } from '../lib/ui';

export default function OrgRegister() {
  const { orgSlug } = useParams();
  const { register } = useAuth();
  const { settings } = useSiteSettings();
  const { productName } = getConfig();

  const [org, setOrg] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);

  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const runIdRef = useRef(0);

  const fetchOrg = useCallback(async () => {
    const id = ++runIdRef.current;
    try {
      const data = await publicApi.getOrgProfile(orgSlug);
      if (id !== runIdRef.current) return;
      setOrg(data);
    } catch {
      if (id !== runIdRef.current) return;
    } finally {
      if (id === runIdRef.current) setOrgLoading(false);
    }
  }, [orgSlug]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  function setField(name) {
    return e => setForm(prev => ({ ...prev, [name]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await register({ ...form, orgSlug });
      setDone(true);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (orgLoading) {
    return (
      <div className={ui.authScreen}>
        <Spinner />
      </div>
    );
  }

  if (!org) {
    return (
      <div className={ui.authScreen}>
        <div className={ui.authCard}>
          <h1 className={ui.h1}>{productName}</h1>
          <div className={ui.alertInfo}>
            This organization is not accepting public registrations. If you have an invitation link,
            please use that instead.
          </div>
          <p className="text-body-s mt-4 text-center">
            Already have an account? <Link to={`/o/${orgSlug}/login`}>Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  const brandName = org.name || productName;

  if (!settings.registrationOpen) {
    return (
      <div className={ui.authScreen}>
        <div className={ui.authCard}>
          <div className={ui.authBrand}>
            {org?.logoUrl && (
              <img src={org.logoUrl} alt={`${brandName} logo`} className={ui.publicOrgLogo} />
            )}
            <h1 className={ui.h1}>{brandName}</h1>
          </div>
          <div className={ui.alertInfo}>Registration is currently closed.</div>
          <p className="text-body-s mt-4 text-center">
            Already have an account? <Link to={`/o/${orgSlug}/login`}>Sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className={ui.authScreen}>
        <div className={ui.authCard}>
          <div className={ui.authBrand}>
            {org?.logoUrl && (
              <img src={org.logoUrl} alt={`${brandName} logo`} className={ui.publicOrgLogo} />
            )}
            <h1 className={ui.h1}>{brandName}</h1>
          </div>
          <div className={ui.alertSuccess}>
            Check your email to verify your account. Once verified, you can sign in.
          </div>
          <p className="text-body-s mt-4 text-center">
            <Link to={`/o/${orgSlug}/login`}>Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={ui.authScreen}>
      <div className={ui.authCard}>
        <div className={ui.authBrand}>
          {org?.logoUrl && (
            <img src={org.logoUrl} alt={`${brandName} logo`} className={ui.publicOrgLogo} />
          )}
          <h1 className={ui.h1}>{brandName}</h1>
          <p className={cn(ui.muted, 'text-body-s')}>Create your account</p>
        </div>

        {error && <ErrorAlert message={error.message || String(error)} />}

        <form onSubmit={onSubmit}>
          <div className={cn(ui.grid, ui.gridMd2)}>
            <Field
              label="First name"
              value={form.firstName}
              onChange={setField('firstName')}
              autoFocus
            />
            <Field label="Last name" value={form.lastName} onChange={setField('lastName')} />
          </div>
          <Field label="Email" type="email" value={form.email} onChange={setField('email')} />
          <Field label="Username" value={form.username} onChange={setField('username')} />
          <Field
            label="Password"
            type="password"
            value={form.password}
            onChange={setField('password')}
          />
          <button
            className={cn(ui.btnPrimary, 'w-full')}
            type="submit"
            disabled={
              busy ||
              !form.email ||
              !form.username ||
              !form.password ||
              !form.firstName ||
              !form.lastName
            }
          >
            {busy ? 'Creating account...' : 'Create account'}
          </button>
        </form>

        <p className="text-body-s mt-4 text-center">
          Already have an account? <Link to={`/o/${orgSlug}/login`}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
